/**
 * Every page's title and description, in both languages.
 *
 * Shared for the same reason src/shared/language.ts is: both halves have to
 * give the same answer. The server writes these into the HTML it sends, and
 * <Seo> re-applies them once React is running. Two copies of the copy would
 * eventually disagree, and the symptom of that disagreement is a page telling
 * Google one thing and a reader another.
 *
 * ── Why the server needs this at all ───────────────────────────────────────
 * This is a client-rendered SPA: before this file existed, every URL was
 * answered with the same index.html, so /es, /support and /es/terms all
 * shipped the front page's <title>, its description and — the expensive one —
 * its <link rel="canonical">. Every page declared itself a duplicate of the
 * home page until JavaScript ran. Google renders JS and mostly recovered, but
 * "mostly" is the problem: Search Console reported a page dropped as
 * "Alternate page with proper canonical tag", which is Google believing the
 * tag we did not mean to send.
 *
 * Keep this free of JSX, of runtime dependencies and of anything Node-specific
 * — it is compiled into the server bundle AND into the browser bundle.
 */
import { DEFAULT_LOCALE, isLocale, type Locale } from './waitlist.js';
import { pathInLocale, stripLocale } from './language.js';

/**
 * The address that actually serves the site, and therefore what canonical
 * tags, hreflang and Open Graph URLs must point at.
 *
 * www, not the bare domain, because twocoins.app redirects to it. A canonical
 * URL that redirects wastes a hop and muddies the signal.
 *
 * src/client/lib/constants.ts re-exports this rather than declaring its own,
 * so the browser and the server cannot drift onto different origins.
 */
export const SITE_ORIGIN = 'https://www.twocoins.app';

/** What the head needs to describe one page in one language. */
export interface PageSeo {
  title: string;
  description: string;
}

/**
 * Every indexable path, without a locale prefix.
 *
 * This is the same list as PAGES in src/client/App.tsx, as public/sitemap.xml
 * and as SOURCE in scripts/prerender.mjs. Adding a page means a line in each;
 * a test asserts this array and the sitemap agree, and the build stops on a
 * page with no source, so neither can be forgotten.
 */
export const PAGE_PATHS = [
  '/',
  '/privacy',
  '/terms',
  '/cookies',
  '/support',
  '/delete-account',
  '/guides',
  '/guides/budget-by-paycheck',
  '/guides/statement-and-due-dates',
  '/guides/interest-free-instalments',
] as const;

export type PagePath = (typeof PAGE_PATHS)[number];

/**
 * The copy itself.
 *
 * Spanish is written natively rather than translated, so the two halves of a
 * row are not word-for-word equivalents and are not meant to be.
 *
 * Titles carry the " — TwoCoins" suffix except on the front page, whose title
 * already names the product. The suffix is what makes a bare search result
 * legible when the page name alone ("Support", "Soporte") says nothing about
 * whose support it is.
 */
const PAGES: Record<PagePath, Record<Locale, PageSeo>> = {
  /*
   * Written for what people type: "app de presupuesto", "controlar gastos",
   * "quincena", "meses sin intereses", "fecha de corte"; "budget app",
   * "paycheck". Titles under 60 characters and descriptions under 155, the
   * lengths a result shows whole.
   */
  '/': {
    en: {
      title: 'TwoCoins — the budget app that plans by paycheck',
      description:
        'What you can spend until your next paycheck, from the money you actually have: cards, bills and statement dates counted. No bank login. 30 days free.',
    },
    es: {
      title: 'TwoCoins — app de presupuesto que entiende tu quincena',
      description:
        'Cuánto puedes gastar hasta tu quincena, con el dinero que sí tienes: tarjetas, meses sin intereses y fechas de corte. Sin tu banco. 30 días gratis.',
    },
  },

  '/privacy': {
    en: {
      title: 'Privacy Policy — TwoCoins',
      description: 'What personal data TwoCoins collects, why, and how to exercise your rights.',
    },
    es: {
      title: 'Aviso de Privacidad — TwoCoins',
      description: 'Qué datos personales recaba TwoCoins, para qué, y cómo ejercer tus derechos.',
    },
  },

  '/terms': {
    en: {
      title: 'Terms of Service — TwoCoins',
      description: 'The terms of the agreement between you and BuildPassing for using TwoCoins.',
    },
    es: {
      title: 'Términos del Servicio — TwoCoins',
      description: 'Los términos del acuerdo entre tú y BuildPassing para el uso de TwoCoins.',
    },
  },

  '/cookies': {
    en: {
      title: 'Cookie Policy — TwoCoins',
      description:
        'TwoCoins does not use cookies. This page explains exactly what is stored and what is not.',
    },
    es: {
      title: 'Política de Cookies — TwoCoins',
      description:
        'TwoCoins no usa cookies. Esta página explica exactamente qué se guarda y qué no.',
    },
  },

  '/support': {
    en: {
      title: 'Support — TwoCoins',
      description:
        'Something broken, something confusing, or something you wish it did? Write to us. A person reads every message, and we answer.',
    },
    es: {
      title: 'Soporte — TwoCoins',
      description:
        '¿Algo se rompió, algo confunde, o algo te gustaría que hiciera? Escríbenos. Una persona lee cada mensaje, y respondemos.',
    },
  },

  '/delete-account': {
    en: {
      title: 'Delete your account — TwoCoins',
      description:
        'You can delete your TwoCoins account and everything attached to it at any time. You do not need our permission and you do not need to explain why.',
    },
    es: {
      title: 'Eliminar tu cuenta — TwoCoins',
      description:
        'Puedes eliminar tu cuenta de TwoCoins y todo lo asociado a ella en cualquier momento. No necesitas nuestro permiso ni tienes que explicar por qué.',
    },
  },

  /*
   * The guides: titled with what people search for, in each market's own
   * words — "presupuesto quincenal", "fecha de corte", "meses sin intereses";
   * "budget by paycheck", "statement closing date". A guide's title here is
   * its heading plus the suffix, and a test holds them together.
   */
  '/guides': {
    en: {
      title: 'Guides: budgeting by paycheck and credit cards — TwoCoins',
      description:
        'Plain guides to budgeting by paycheck, statement closing and due dates, and interest-free instalment plans: how each one works and what it costs.',
    },
    es: {
      title: 'Guías de presupuesto quincenal y tarjetas — TwoCoins',
      description:
        'Guías claras sobre presupuesto quincenal, fecha de corte y fecha límite de pago, y meses sin intereses: cómo funciona cada uno y cuánto cuesta.',
    },
  },

  '/guides/budget-by-paycheck': {
    en: {
      title: 'How to budget by paycheck — TwoCoins',
      description:
        'A step-by-step way to plan each paycheck: what is already spoken for, what to save, and how much you can spend per day until the next one. With an example.',
    },
    es: {
      title: 'Cómo hacer un presupuesto quincenal — TwoCoins',
      description:
        'Paso a paso para organizar tu quincena: qué ya está comprometido, cuánto ahorrar y cuánto puedes gastar al día hasta el siguiente pago. Con ejemplo.',
    },
  },

  '/guides/statement-and-due-dates': {
    en: {
      title: 'Statement closing date vs. due date — TwoCoins',
      description:
        'What a credit card’s closing date and due date mean, which amount to pay to avoid interest, and the best day of the month to make a big purchase.',
    },
    es: {
      title: 'Fecha de corte y fecha límite de pago — TwoCoins',
      description:
        'Qué son la fecha de corte y la fecha límite de pago de tu tarjeta, cuál pago evita intereses y cuál es el mejor día para hacer una compra grande.',
    },
  },

  '/guides/interest-free-instalments': {
    en: {
      title: 'Interest-free instalments: how they really work — TwoCoins',
      description:
        'Interest-free instalment plans, buy now pay later and deferred interest: how each uses your credit limit, what it costs, and when it stops being free.',
    },
    es: {
      title: 'Meses sin intereses: cómo funcionan de verdad — TwoCoins',
      description:
        'Cómo funcionan los meses sin intereses: cuánto de tu línea de crédito ocupan, qué pasa si no pagas a tiempo y cuándo conviene más pagar de contado.',
    },
  },
};

/** Whether a locale-free path is one of the real pages. */
export function isPagePath(path: string): path is PagePath {
  return Object.prototype.hasOwnProperty.call(PAGES, path);
}

/**
 * Split a request path into the language it asks for and the page it names.
 *
 * A trailing slash is not a different page: /es/ and /es are the same request,
 * and answering them with different canonicals is one of the ways a site
 * competes with itself. Normalising here means the server, the client and the
 * tests cannot disagree about it.
 */
export function routeFor(pathname: string): { locale: Locale; path: string } {
  const first = pathname.split('/').filter(Boolean)[0];
  const locale: Locale = isLocale(first) ? first : DEFAULT_LOCALE;
  return { locale, path: stripLocale(pathname) };
}

/** The strings for one page in one language, or null if the path is not a page. */
export function seoFor(path: string, locale: Locale): PageSeo | null {
  if (!isPagePath(path)) return null;
  return PAGES[path][locale] ?? null;
}

/** Where a locale-free path lives in a given language. */
export function pathFor(path: PagePath, locale: Locale): string {
  // One rule for where a page lives in a language — a guide's Spanish address included.
  return pathInLocale(path, locale);
}

/** The absolute URL a page should name as its own. */
export function canonicalFor(path: PagePath, locale: Locale): string {
  return `${SITE_ORIGIN}${pathFor(path, locale)}`;
}

/**
 * The hreflang set for a page.
 *
 * x-default names the version to serve anyone the other two do not cover,
 * which is English — the same answer the language middleware falls back to.
 */
export function alternatesFor(path: PagePath): { hreflang: string; href: string }[] {
  const en = canonicalFor(path, 'en');
  return [
    { hreflang: 'en', href: en },
    { hreflang: 'es', href: canonicalFor(path, 'es') },
    { hreflang: 'x-default', href: en },
  ];
}

/**
 * The share card.
 *
 * One image per language, because it carries a line of copy and the brand rule
 * is one language per piece.
 */
export function ogImageFor(locale: Locale): string {
  return `${SITE_ORIGIN}${locale === 'es' ? '/og-image-es.png' : '/og-image.png'}`;
}

/** The Open Graph locale code, which is not the same shape as ours. */
export function ogLocaleFor(locale: Locale): string {
  return locale === 'es' ? 'es_MX' : 'en_US';
}
